<template>
    <div class="nevs-content">
        <div class="nevs-content-top-buttons">
            <NevsButton class="primary" @click="addClick">
                <i class="fa-solid fa-plus"></i>
                {{ $LANG.Get('buttons.add') }}
            </NevsButton>
        </div>
        <div v-if="quota !== null" class="notifications-quota">
            <i class="fa-solid fa-circle-info"></i> {{ quotaText }}
        </div>
        <div v-if="quotaExceeded" class="notifications-quota-exceeded">
            <i class="fa-solid fa-triangle-exclamation"></i> {{ quotaExceededText }}
        </div>
        <NevsCard>
            <NevsTable
                :default-sort="tableData.defaultSort"
                :fields="tableData.fields"
                :height="tableData.height"
                :total-records="tableData.totalRecords"
                @reload="reloadTable">
                <tr class="nevs-table-filters">
                    <td>
                        <NevsTextField v-model="tableData.filters.name"></NevsTextField>
                    </td>
                    <td v-show="!$store.state.settings.MOBILE_TEMP_DISABLED">
                        <NevsSelect v-model="tableData.filters.channel" :options="channelOptions"></NevsSelect>
                    </td>
                    <td></td>
                    <td>
                        <NevsSelect v-model="tableData.filters.status" :options="statusOptions"></NevsSelect>
                    </td>
                    <td></td>
                </tr>
                <tr v-for="(item, key) in tableData.records" :key="key">
                    <td class="nevs-table-linked-cell" @click="openClick(item.id)">{{ item.name }}</td>
                    <td v-show="!$store.state.settings.MOBILE_TEMP_DISABLED" class="nevs-table-linked-cell" @click="openClick(item.id)">
                        <span class="channel-pill" :class="'channel-pill-' + item.channel">
                            {{ $LANG.Get('notificationChannels.' + item.channel) }}
                        </span>
                    </td>
                    <td class="nevs-table-linked-cell" @click="openClick(item.id)">{{ item.time_display }}</td>
                    <td class="nevs-table-linked-cell" @click="openClick(item.id)">
                        <span class="status-pill" :class="'status-pill-' + item.status_key">
                            {{ $LANG.Get('notificationStatuses.' + item.status_key) }}
                        </span>
                    </td>
                    <td class="nevs-table-actions">
                        <span :title="$LANG.Get('tooltips.openInNewWindow')" class="nevs-table-button"
                            @click="openClick(item.id, true)"><i class="fa-solid fa-up-right-from-square"></i></span>
                        <span v-if="!item.sent" :title="$LANG.Get('tooltips.delete')"
                            class="nevs-table-button nevs-table-button-delete"
                            @click="deleteClick(item.id)"><i class="fa-solid fa-trash"></i></span>
                    </td>
                </tr>
            </NevsTable>
        </NevsCard>
    </div>
</template>

<script>
import NevsCard from "@/components/nevs/NevsCard.vue";
import NevsButton from "@/components/nevs/NevsButton.vue";
import NevsTable from "@/components/nevs/NevsTable.vue";
import NevsTextField from "@/components/nevs/NevsTextField.vue";
import NevsSelect from "@/components/nevs/NevsSelect.vue";

export default {
    name: "ModuleNotifications",
    components: {NevsSelect, NevsTextField, NevsTable, NevsButton, NevsCard},
    data() {
        return {
            tableData: {
                fields: [
                    {
                        name: 'name',
                        label: this.$LANG.Get('fields.name')
                    },
                    // Left out while the members app is not yet on the app stores (its cells are hidden to match).
                    ...(this.$store.state.settings.MOBILE_TEMP_DISABLED ? [] : [{
                        name: 'channel',
                        width: '170px',
                        label: this.$LANG.Get('fields.notificationChannel')
                    }]),
                    {
                        name: 'time',
                        label: this.$LANG.Get('fields.sendTime')
                    },
                    {
                        name: 'status',
                        width: '220px',
                        label: this.$LANG.Get('fields.status')
                    },
                    {
                        name: 'actions',
                        width: '50px',
                        label: ''
                    }
                ],
                filters: {
                    name: '',
                    channel: -1,
                    status: -1
                },
                records: [],
                lastRequest: null,
                totalRecords: 0,
                defaultSort: {
                    field: 'time',
                    descending: true
                },
                filterTimer: null,
                height: '0px'
            },
            quota: null,
            crossTabHandle: null
        }
    },
    computed: {
        channelOptions() {
            return [
                {value: 'email', label: this.$LANG.Get('notificationChannels.email')},
                {value: 'mobile', label: this.$LANG.Get('notificationChannels.mobile')},
                {value: -1, label: this.$LANG.Get('options.all')}
            ];
        },
        statusOptions() {
            // Values match the status codes the API maps back onto (active, sent) combinations.
            let statuses = [
                {value: 1, key: 'sentAutomatically'},
                {value: 2, key: 'sentManually'},
                {value: 3, key: 'waitingAutomatic'},
                {value: 4, key: 'inactive'}
            ];
            let options = statuses.map((status) => ({
                value: status.value,
                label: this.$LANG.Get('notificationStatuses.' + status.key)
            }));
            options.push({value: -1, label: this.$LANG.Get('options.all')});
            return options;
        },
        noMobileSuffix() {
            // While the members app is not yet on the app stores, the texts leave out what it would add.
            return this.$store.state.settings.MOBILE_TEMP_DISABLED ? 'NoMobile' : '';
        },
        quotaExceeded() {
            return this.quota !== null && this.quota.sent >= this.quota.limit;
        },
        quotaText() {
            if (this.quota === null) return '';
            return this.$LANG.Get('labels.notificationQuota' + this.noMobileSuffix)
                .replaceAll('%sent%', this.quota.sent)
                .replaceAll('%limit%', this.quota.limit);
        },
        quotaExceededText() {
            if (this.quota === null) return '';
            return this.$LANG.Get('labels.notificationQuotaExceeded' + this.noMobileSuffix)
                .replaceAll('%limit%', this.quota.limit);
        }
    },
    watch: {
        'tableData.filters': {
            handler() {
                if (this.filterTimer !== null) {
                    clearTimeout(this.filterTimer);
                }
                let vm = this;
                this.filterTimer = setTimeout(function () {
                    vm.reloadTable(vm.tableData.lastRequest);
                    vm.filterTimerOn = false;
                }, 500);
            },
            deep: true
        }
    },
    methods: {
        openClick(id, newWindow = false) {
            if (newWindow) {
                let routeData = this.$router.resolve('/notifications/' + id);
                window.open(routeData.href);
            } else {
                this.$router.push('/notifications/' + id);
            }
        },
        addClick() {
            this.$router.push('/notifications/0');
        },
        deleteClick(id) {
            let vm = this;
            this.$LOCAL_BUS.TriggerEvent('popup', {
                type: 'confirm', text: this.$LANG.Get('alerts.generalRemovalQuestion'), callback: (response) => {
                    if (response) {
                        vm.$API.APICall('delete', 'notifications/' + id, {}, (data, success) => {
                            if (success) {
                                // The cross-tab reload listener refreshes this grid (and other open tabs).
                                vm.$CROSS_TAB_BUS.TriggerEvent('reload-notifications', {});
                            } else {
                                vm.$HELPERS.HandleServerError(vm, data);
                            }
                        });
                    }
                }
            });
        },
        reloadTable(request) {
            let vm = this;
            request.filters = this.tableData.filters;
            this.tableData.lastRequest = JSON.parse(JSON.stringify(request));
            this.$API.APICall('get', 'notifications', request, (data, success) => {
                if (success) {
                    vm.tableData.records = data.records;
                    vm.tableData.totalRecords = data.total_records;
                } else {
                    vm.$HELPERS.HandleServerError(vm, data);
                }
            });
        },
        loadQuota() {
            let vm = this;
            this.$API.APICall('get', 'notifications/quota', {}, (data, success) => {
                if (success) {
                    vm.quota = data;
                }
            });
        },
        resolveWindowResize() {
            this.tableData.height = window.innerHeight - 205 + 'px';
        }
    },
    mounted() {
        this.$store.commit('selectMenu', 'clients');
        this.$store.commit('selectSubMenu', 'notifications');
        this.$store.commit('setBreadcrumbs', [
            {
                label: this.$LANG.Get('modules.clients'),
                link: null
            },
            {
                label: this.$LANG.Get('modules.notifications'),
                link: null
            }
        ]);
        window.addEventListener('resize', this.resolveWindowResize);
        this.resolveWindowResize();
        this.loadQuota();
        let vm = this;
        this.crossTabHandle = this.$CROSS_TAB_BUS.ListenToEvent('reload-notifications', () => {
            vm.reloadTable(vm.tableData.lastRequest);
            vm.loadQuota();
        });
    },
    unmounted() {
        if (this.crossTabHandle !== null) {
            this.$CROSS_TAB_BUS.UnbindEvent(this.crossTabHandle);
        }
    }
}
</script>

<style scoped lang="scss">
@use '../../scss/vars' as *;

.notifications-quota {
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: $peach-wash;
    border: 1px solid $sand-300;
    color: $ink;
    font-size: 14px;
}

.notifications-quota-exceeded {
    margin: 0 0 12px;
    padding: 10px 12px;
    border-radius: 8px;
    background: $danger-surface;
    border: 1px solid $danger;
    color: $ink-strong;
    font-size: 14px;
    font-weight: 600;
}

.channel-pill {
    display: inline-block;
    padding: 5px 10px;
    border-radius: 5px;
    background: $sand-300;
}

.channel-pill-mobile {
    background: $peach-wash;
    border: 1px solid $sand-300;
}

.status-pill {
    display: inline-block;
    padding: 5px 10px;
    border-radius: 5px;
    background: $sand-300;
}

.status-pill-sentAutomatically,
.status-pill-sentManually {
    background: $success-badge;
}

.status-pill-waitingAutomatic {
    background: $warning-surface;
}

.status-pill-inactive {
    background: $sand-300;
}

.nevs-table-button-delete:hover {
    color: $danger;
}
</style>
